import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ShieldCheck,
  ArrowRight,
  Loader2,
  Plus,
  Sparkles,
  BadgeCheck,
  X,
} from "lucide-react";

/**
 * First-scan safety-net card. Rendered inline (not modal, not toast) below
 * the FindCustomers action bar when the backend attaches `safety_net` to a
 * scan response. Stays visible until the user acts on it (widen scope OR add
 * a keyword and retry).
 *
 * Props:
 *   safetyNet    — payload from POST /api/scans: {type, from_scope, to_scope,
 *                  reason_headline, reason_detail, action_label, keyword_suggestions?}
 *                  keyword_suggestions are AI-generated (OpenRouter) from the
 *                  brand + its existing keywords — not hardcoded.
 *   brandId      — needed for the add-keyword endpoint
 *   onScopeWidened({ scope, city }) — called after we successfully switch scope
 *   onKeywordAdded(text) — called after we successfully add a keyword
 *   onDismiss()  — hide the card without acting
 */
export default function FirstScanSafetyNet({
  safetyNet,
  brandId,
  onScopeWidened,
  onKeywordAdded,
  onDismiss,
}) {
  const [busy, setBusy] = useState(false);
  const [busyKw, setBusyKw] = useState(null); // which chip/custom is submitting
  const [customKw, setCustomKw] = useState("");

  if (!safetyNet) return null;

  const isScopeSwitch = safetyNet.type === "switch_scope";
  const isAddKeyword = safetyNet.type === "add_keyword";

  const doScopeSwitch = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/onboarding/select-market-scope", { scope: safetyNet.to_scope });
      toast.success(`Switched to ${labelForScope(safetyNet.to_scope)}. Retrying with a wider net…`);
      onScopeWidened?.({ scope: safetyNet.to_scope });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch scope.");
    } finally {
      setBusy(false);
    }
  };

  const doAddKeyword = async (text) => {
    const kw = (text || "").trim();
    if (!kw || busy) return;
    setBusy(true);
    setBusyKw(kw);
    try {
      await api.post(`/brands/${brandId}/keywords`, { text: kw });
      toast.success(`Added "${kw}". Retrying with the wider keyword set…`);
      setCustomKw("");
      onKeywordAdded?.(kw);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add keyword.");
    } finally {
      setBusy(false);
      setBusyKw(null);
    }
  };

  return (
    <div
      data-testid="first-scan-safety-net"
      className="mb-6 relative overflow-hidden rounded-2xl border border-lm-border bg-lm-surface shadow-[0_16px_40px_-24px_rgba(31,74,56,0.35)]"
    >
      {/* Top accent band — reassuring green, since the credit was refunded */}
      <div className="h-1.5 w-full bg-gradient-to-r from-lm-primary via-emerald-500 to-lm-secondary" />

      <div className="p-5 md:p-6">
        {/* Header row */}
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lm-primary to-emerald-600 text-white flex items-center justify-center shadow-md">
              <ShieldCheck className="w-6 h-6" strokeWidth={2} />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-mono uppercase tracking-widest text-lm-primary">
                First-scan safety net
              </span>
              {safetyNet.refunded && (
                <span
                  data-testid="first-scan-safety-refunded"
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                >
                  <BadgeCheck className="w-3 h-3" /> Credit refunded
                </span>
              )}
            </div>
            <h3 className="text-lg md:text-xl font-display font-semibold tracking-tight text-lm-text mt-1">
              {safetyNet.reason_headline}
            </h3>
            <p className="text-sm text-lm-text2 mt-1 max-w-2xl leading-relaxed">
              {safetyNet.reason_detail}
            </p>
          </div>

          <button
            onClick={onDismiss}
            data-testid="first-scan-safety-close"
            aria-label="Dismiss"
            className="shrink-0 -mt-1 -mr-1 p-1.5 rounded-lg text-lm-text3 hover:text-lm-text hover:bg-lm-tint transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scope-switch action */}
        {isScopeSwitch && (
          <div className="mt-5 pl-0 md:pl-16 flex flex-wrap items-center gap-3">
            <button
              onClick={doScopeSwitch}
              disabled={busy}
              data-testid="first-scan-safety-widen"
              className="group inline-flex items-center gap-2 bg-lm-primary text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-lm-primaryHover disabled:opacity-60 shadow-sm transition-all hover:-translate-y-0.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {safetyNet.action_label}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={onDismiss}
              data-testid="first-scan-safety-dismiss"
              className="text-xs text-lm-text3 hover:text-lm-text2 underline underline-offset-2"
            >
              Not now
            </button>
          </div>
        )}

        {/* Add-keyword action */}
        {isAddKeyword && (
          <div className="mt-5 md:pl-16" data-testid="first-scan-safety-addkw">
            {(safetyNet.keyword_suggestions || []).length > 0 && (
              <div className="mb-4 rounded-xl border border-lm-border bg-lm-tint2/30 p-3.5">
                <div className="flex items-center gap-1.5 mb-2.5">
                  <Sparkles className="w-3.5 h-3.5 text-lm-primary" />
                  <span className="text-[11px] font-mono uppercase tracking-widest text-lm-primary">
                    AI-picked for your brand
                  </span>
                  <span className="text-[11px] text-lm-text3">· tap one to add &amp; retry</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {safetyNet.keyword_suggestions.map((k) => {
                    const thisBusy = busyKw === k;
                    return (
                      <button
                        key={k}
                        onClick={() => doAddKeyword(k)}
                        disabled={busy}
                        data-testid={`safety-suggested-kw-${k.replace(/\s+/g, "-")}`}
                        className="group inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-lm-border bg-lm-surface hover:border-lm-primary hover:bg-lm-tint2 text-lm-text disabled:opacity-50 transition-all hover:-translate-y-0.5 shadow-sm"
                      >
                        {thisBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-lm-primary" />
                        ) : (
                          <Plus className="w-3.5 h-3.5 text-lm-primary" />
                        )}
                        {k}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={customKw}
                onChange={(e) => setCustomKw(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doAddKeyword(customKw)}
                placeholder="Or type your own — e.g. 'crm alternative'"
                data-testid="safety-custom-kw-input"
                className="flex-1 border border-lm-border rounded-lg px-4 py-2.5 bg-lm-bg text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary transition-shadow"
              />
              <button
                onClick={() => doAddKeyword(customKw)}
                disabled={busy || !customKw.trim()}
                data-testid="safety-custom-kw-submit"
                className="inline-flex items-center justify-center gap-2 bg-lm-primary text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-lm-primaryHover disabled:opacity-50 shadow-sm transition-all"
              >
                {busy && busyKw === customKw.trim() ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add &amp; retry
              </button>
            </div>

            <button
              onClick={onDismiss}
              data-testid="first-scan-safety-dismiss"
              className="mt-3 text-xs text-lm-text3 hover:text-lm-text2 underline underline-offset-2"
            >
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function labelForScope(s) {
  switch (s) {
    case "india": return "All India";
    case "us": return "United States";
    case "uk": return "United Kingdom";
    case "au": return "Australia";
    case "global": return "Global";
    case "local_city": return "My city";
    default: return s;
  }
}
